import { AnyAction, combineReducers, Reducer } from "redux";
import produce from "immer";

import { IPicassoAction, reducer as picasso } from "@multiweb/picasso";
import { createInstanceMapReducer, EMPTY_ARRAY, EMPTY_OBJECT, invariant } from "@multiweb/reactUtils";
import { MutableTypedObject, TypedObject } from "@multiweb/types/react";
import { ParticipantType, WorkflowEditorMode } from "./enums";
import {
    IActivity,
    IActivityPropertyPanelState,
    IClearPicassoHostAction,
    IEnableStatusViewAction,
    IEnableViewerModeAction,
    IGeneralPropertyPanelState,
    IHideMessageWindowAction,
    IHostInstanceState,
    ILoadWorkflowEntities,
    IParticipant,
    IPicassoHostAction,
    ISet,
    ISetTouchedAction,
    IShowMessageWindowAction,
    IToggleEditDialogsVisibilityAction,
    IToggleGridAction,
    ITransition,
    ITransitionPropertyPanelState,
    IUpdateActivityAction,
    IUpdateActivityPropertyPanelStateAction,
    IUpdateDefinitionParticipantsAction,
    IUpdateGeneralPropertyPanelStateAction,
    IUpdateSetsAction,
    IUpdateTransitionAction,
    IUpdateTransitionPropertyPanelStateAction,
    IVariable,
    IWorkflowEditorState,
    WfMessageWindowState,
} from "./types";
import { updateActions, updateTriggers } from "./utils";

/** Defaultní stav pro Activty panel */
const ACTIVITY_PROPERTY_PANEL_STATE: IActivityPropertyPanelState = {
    "PROPERTIES": { isOpen: true },
    "ACTIONS": { isOpen: true },
    "TRIGGERS": { isOpen: true },
    "PARTICIPANTS": { isOpen: true },
    "ACTIVE_PARTICIPANTS": { isOpen: true },
    "SETS": { isOpen: true },
};

/** Defaultní stav pro Transition panel */
const TRANSITION_PROPERTY_PANEL_STATE: ITransitionPropertyPanelState = {
    "PROPERTIES": { isOpen: true },
    "ACTIONS": { isOpen: true },
};

/** Defaultní stav pro General panel */
const GENERAL_PROPERTY_PANEL_STATE: IGeneralPropertyPanelState = {
    "VARIABLES": { isOpen: true },
    "LEGEND": { isOpen: true },
    "SET": { isOpen: true },
};

const WF_MESSAGE_WINDOW_DEFAULT_STATE: WfMessageWindowState = {
    actionId: null,
    contentLocKey: null,
    isVisible: false,
    primaryButtonLocKey: null,
    secondaryButtonLocKey: null,
};

const wfEditorMode: Reducer<WorkflowEditorMode> = (state = WorkflowEditorMode.Editor, action) => {
    if (action instanceof IEnableStatusViewAction)
        return WorkflowEditorMode.StatusViewer;
    if (action instanceof IEnableViewerModeAction)
        return WorkflowEditorMode.Viewer;
    return state;
};

const isGridEnabled: Reducer<boolean> = (state = true, action) => {
    if (action instanceof IToggleGridAction)
        return !state;
    if (action instanceof IEnableStatusViewAction)
        return false;

    return state;
};

const isEditDialogHidden: Reducer<boolean> = (state = false, action) => {
    if (action instanceof IToggleEditDialogsVisibilityAction)
        return !state;

    return state;
};

const touched: Reducer<boolean> = (state = false, action) => {
    if (action instanceof ISetTouchedAction)
        return action.payload.touched;

    return state;
};

const selectedSetId: Reducer<string> = (state = "", action) => {
    if (action instanceof ILoadWorkflowEntities)
        return action.payload.selectedSetId ?? state;

    return state;
};

const orderedSetsIds: Reducer<readonly string[]> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof ILoadWorkflowEntities)
        return action.payload.sets.map(set => set.id);
    else if (action instanceof IUpdateSetsAction)
        return action.payload.newSets.map(set => set.id);

    return state;
};

const sets: Reducer<TypedObject<ISet>> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ILoadWorkflowEntities) {
        const newSets: MutableTypedObject<ISet> = {};
        action.payload.sets.forEach((set) => (newSets[set.id] = set));
        return newSets;
    } else if (action instanceof IUpdateSetsAction) {
        const newSets: MutableTypedObject<ISet> = {};
        action.payload.newSets.forEach((set) => (newSets[set.id] = set));
        return newSets;
    }

    return state;
};

const activities: Reducer<readonly IActivity[]> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof ILoadWorkflowEntities) {
        return action.payload.activities.map(activity => {
            return {
                ...activity,
                actions: updateActions(activity.actions),
                triggers: updateTriggers(activity.triggers),
            };
        });
    } else if (action instanceof IUpdateActivityAction) {
        return produce(state, (draft: IActivity[]): void => {
            const { picassoIndex, newActivity } = action.payload;
            const index = picassoIndex - 1; // picaso na prvním místě má vždy komponentu sady, proto zde - 1

            invariant(index >= 0 && index < state.length, "Index is out of bounds. Throwing error to avoid data corruption.");

            const { actions: oldActions, triggers: oldTriggers } = state[index];
            const { actions: newActions, triggers: newTriggers } = newActivity;

            if (oldActions !== newActions || oldTriggers !== newTriggers) {
                const updatedActivity = { ...newActivity };

                if (oldActions !== newActions)
                    updatedActivity.actions = updateActions(newActions, oldActions);
                if (oldTriggers !== newTriggers)
                    updatedActivity.triggers = updateTriggers(newTriggers, oldTriggers);

                draft[index] = updatedActivity;
            } else
                draft[index] = newActivity;
        });
    } else if (action instanceof IUpdateSetsAction) {
        return produce(state, (draft) => {
            const { newSets } = action.payload;
            draft.forEach(activity => {
                // pokud byla sada odebrána, smaž referenci z aktivity
                activity.positions = activity.positions.filter(position => newSets.some(newSet => newSet.id === position.setId));
                for (const position of activity.positions) {
                    const newSet = newSets.find(set => set.id === position.setId);

                    if (position.setId === newSet?.id) {
                        position.setName = newSet.name;
                        return;
                    }
                }
            });
        });
    } else if (action instanceof IUpdateDefinitionParticipantsAction) {
        const { newActivities, newParticipants: newDefinitionParticipants } = action.payload;

        if (!newActivities) {
            return state;
        }

        return produce(state, (draft) => {
            for (const newActivity of newActivities) {
                const draftActivity = draft.find(x => x.id === newActivity.id);

                if (!draftActivity) {
                    continue;
                }

                draftActivity.participants = draftActivity.participants
                    .filter(x => x.participantType !== ParticipantType.DefinitionParticipant || newDefinitionParticipants.some(y => y.identifier === x.identifier))
                    .map(participant => newDefinitionParticipants.find(x => x.identifier === participant.identifier) ?? participant);
            }
        });
    }

    return state;
};

const transitions: Reducer<readonly ITransition[]> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof ILoadWorkflowEntities)
        return action.payload.transitions.map(transition => {
            return { ...transition, actions: updateActions(transition.actions) };
        });
    else if (action instanceof IUpdateTransitionAction) {
        return produce(state, (draft: ITransition[]) => {
            const { index, newTransition } = action.payload;
            const oldActions = state[index].actions;
            const newActions = newTransition.actions;

            if (oldActions !== newActions)
                draft[index] = { ...newTransition, actions: updateActions(newActions, oldActions) };
            else
                draft[index] = newTransition;
        });
    }

    return state;
};

const activityPropertyPanelState: Reducer<IActivityPropertyPanelState> = (state = ACTIVITY_PROPERTY_PANEL_STATE, action) => {
    if (action instanceof IUpdateActivityPropertyPanelStateAction) {
        return produce(state, (draft) => {
            const { isExpanded, containerName } = action.payload;
            draft[containerName].isOpen = isExpanded;
        });
    }

    return state;
};

const transitionPropertyPanelState: Reducer<ITransitionPropertyPanelState> = (state = TRANSITION_PROPERTY_PANEL_STATE, action) => {
    if (action instanceof IUpdateTransitionPropertyPanelStateAction) {
        return produce(state, (draft) => {
            const { isExpanded, containerName } = action.payload;
            draft[containerName].isOpen = isExpanded;
        });
    }

    return state;
};

const generalPropertyPanelState: Reducer<IGeneralPropertyPanelState> = (state = GENERAL_PROPERTY_PANEL_STATE, action) => {
    if (action instanceof IUpdateGeneralPropertyPanelStateAction) {
        return produce(state, (draft) => {
            const { isExpanded, containerName } = action.payload;
            draft[containerName].isOpen = isExpanded;
        });
    }

    return state;
};

const definitionParticipants: Reducer<readonly IParticipant[]> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof ILoadWorkflowEntities)
        return action.payload.definitionParticipants;
    else if (action instanceof IUpdateDefinitionParticipantsAction)
        return action.payload.newParticipants;

    return state;
};

const variables: Reducer<readonly IVariable[]> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof IEnableStatusViewAction)
        return action.payload.variables;

    return state;
};

const wfMessageWindowState: Reducer<WfMessageWindowState> = (state = WF_MESSAGE_WINDOW_DEFAULT_STATE, action) => {
    if (action instanceof IShowMessageWindowAction)
        return { ...action.payload, isVisible: true };
    if (action instanceof IHideMessageWindowAction)
        return WF_MESSAGE_WINDOW_DEFAULT_STATE;

    return state;
};

const hostReducer: Reducer<IHostInstanceState> = combineReducers({
    picasso,
    wfEditorMode,
    isGridEnabled,
    isEditDialogHidden,
    touched,
    selectedSetId,
    orderedSetsIds,
    sets,
    activities,
    transitions,
    activityPropertyPanelState,
    transitionPropertyPanelState,
    generalPropertyPanelState,
    definitionParticipants,
    variables,
    wfMessageWindowState,
});

const instanceKeyResolver = (action: AnyAction) => {
    if (action instanceof IPicassoHostAction)
        return action.meta.hostKey;
    if (action instanceof IPicassoAction)
        return action.meta.picassoInstanceKey;

    return undefined;
};

const instanceMapReducer = createInstanceMapReducer(hostReducer, instanceKeyResolver);

const instances: Reducer<TypedObject<IHostInstanceState>> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IClearPicassoHostAction)
        return produce(state, (draft) => {
            delete draft[action.meta.hostKey];
        });

    return instanceMapReducer(state, action);
};

export const reducer: Reducer<IWorkflowEditorState> = combineReducers({
    instances,
});
